import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Search, ChevronRight } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/lib/components/ui/button";
import { Input } from "@/lib/components/ui/input";
import { Badge } from "@/lib/components/ui/badge";
import { Card } from "@/lib/components/ui/card";
import { Skeleton } from "@/lib/components/ui/skeleton";
import {
  fetchFilamentBrands,
  fetchFilamentMaterials,
  fetchFilaments,
  addFilamentToCart,
} from "@/lib/catalogApi";
import { useCart } from "@/features/cart/hooks/useCart";
import { formatCurrency } from "@/lib/format";
import type {
  RetailFilamentRoll,
  RetailFilamentBrand,
  RetailFilamentMaterial,
} from "@/types/catalog";
import clsx from "clsx";

/** Max filaments shown per material section in the "All" grouped view. */
const SECTION_CAP = 6;
const SKELETON_COUNT = 6;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDiscountedPrice(grossPrice: number, discountPercent: number): number {
  return Math.round(grossPrice * (1 - discountPercent / 100) * 100) / 100;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FilamentCardSkeleton() {
  return (
    <Card className="overflow-hidden bg-card/90 border-border shadow-sm p-0">
      <Skeleton className="aspect-square w-full rounded-none" />
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Skeleton className="mt-0.5 h-4 w-4 rounded-full shrink-0" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-10 w-full" />
      </div>
    </Card>
  );
}

interface FilamentCardProps {
  roll: RetailFilamentRoll;
  qty: number;
  onQtyChange: (qty: number) => void;
  onAddToCart: () => void;
}

function FilamentCard({ roll, qty, onQtyChange, onAddToCart }: FilamentCardProps) {
  const { t } = useTranslation("catalog");
  const hasDiscount = roll.discount_percent > 0;
  const discountedPrice = hasDiscount
    ? getDiscountedPrice(roll.gross_price, roll.discount_percent)
    : roll.gross_price;
  const primaryImage = roll.images?.[0];

  return (
    <Card className="overflow-hidden bg-card/90 border-border shadow-sm hover:shadow-md transition-shadow p-0">
      <Link to={`/filaments/${roll.slug}`} className="block">
        <div className="relative aspect-square bg-muted">
          {primaryImage ? (
            <img
              src={primaryImage.url}
              alt={primaryImage.alt ?? roll.color_name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className="w-full h-full"
              style={{ backgroundColor: roll.color_hex || "#cccccc" }}
            />
          )}
          {hasDiscount && (
            <div className="absolute top-2 right-2">
              <Badge className="bg-destructive text-destructive-foreground">
                {roll.discount_percent}% {t("filaments.off")}
              </Badge>
            </div>
          )}
        </div>
      </Link>

      <div className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <span
            className="mt-0.5 shrink-0 inline-block h-4 w-4 rounded-full border border-border"
            style={{ backgroundColor: roll.color_hex || "#cccccc" }}
            aria-label={roll.color_name}
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight truncate">
              {roll.name ?? roll.color_name}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {roll.brand?.name} · {roll.material?.name} · {roll.weight_grams}g
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasDiscount ? (
            <>
              <span className="text-lg font-bold">{formatCurrency(discountedPrice)}</span>
              <span className="text-sm line-through text-muted-foreground">
                {formatCurrency(roll.gross_price)}
              </span>
            </>
          ) : (
            <span className="text-lg font-bold">{formatCurrency(roll.gross_price)}</span>
          )}
          <span className="text-xs text-muted-foreground ml-auto">{t("filaments.inclVat")}</span>
        </div>

        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            value={qty}
            onChange={e => onQtyChange(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-16 text-center"
          />
          <Button className="flex-1" onClick={onAddToCart}>
            {t("filaments.addToCart")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type MaterialSection = {
  material: RetailFilamentMaterial;
  rolls: RetailFilamentRoll[];
  hasMore: boolean;
};

export default function FilamentsPage() {
  const { t } = useTranslation("catalog");
  const [searchParams, setSearchParams] = useSearchParams();
  const [brands, setBrands] = useState<RetailFilamentBrand[]>([]);
  const [materials, setMaterials] = useState<RetailFilamentMaterial[]>([]);
  const [rolls, setRolls] = useState<RetailFilamentRoll[]>([]);
  const [search, setSearch] = useState("");
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const { increment } = useCart();

  // ── URL-derived filter state ─────────────────────────────────────────────
  // Brand is identified by slug in the URL (?brand=smartfill).
  // Material is identified by its numeric ID (?material=3).
  const brandSlugParam = searchParams.get("brand");
  const materialIdParam = searchParams.get("material") ?? "all";

  // Derive the active brand object from the URL param + loaded brands list.
  // Falls back to the first brand so there is always a selection.
  const selectedBrand = useMemo<RetailFilamentBrand | null>(() => {
    if (brands.length === 0) return null;
    return brands.find(b => b.slug === brandSlugParam) ?? brands[0];
  }, [brands, brandSlugParam]);

  const selectedMaterialId = materialIdParam;

  // ── Brand / material selection handlers ──────────────────────────────────
  const handleSelectBrand = (brand: RetailFilamentBrand) => {
    // Clear the material filter whenever the brand changes.
    setSearchParams({ brand: brand.slug }, { replace: false });
  };

  const handleSelectMaterial = (materialId: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (materialId === "all") {
          next.delete("material");
        } else {
          next.set("material", materialId);
        }
        return next;
      },
      { replace: false },
    );
  };

  // ── Load brands on mount ─────────────────────────────────────────────────
  useEffect(() => {
    setBrandsLoading(true);
    fetchFilamentBrands()
      .then(b => {
        setBrands(b);
        // If the URL has no ?brand param yet, set it to the first brand so
        // the URL is always canonical and shareable from the first page load.
        if (b.length > 0 && !searchParams.get("brand")) {
          setSearchParams({ brand: b[0].slug }, { replace: true });
        }
      })
      .catch(() => toast.error(t("filaments.failedToLoadBrands")))
      .finally(() => setBrandsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Reload materials when the active brand changes ───────────────────────
  useEffect(() => {
    if (!selectedBrand) return;
    fetchFilamentMaterials(selectedBrand.slug)
      .then(setMaterials)
      .catch(() => setMaterials([]));
  }, [selectedBrand]);

  // ── Reload rolls when any filter changes ─────────────────────────────────
  // Wait for brands to resolve first so we never fire an unfiltered fetch.
  useEffect(() => {
    if (brandsLoading) return;
    setLoading(true);
    const params: { brand_id?: number; material_id?: number; search?: string } = {};
    if (selectedBrand) params.brand_id = selectedBrand.id;
    if (selectedMaterialId !== "all") params.material_id = parseInt(selectedMaterialId);
    if (search) params.search = search;
    fetchFilaments(params)
      .then(setRolls)
      .catch(() => toast.error(t("filaments.failedToLoad")))
      .finally(() => setLoading(false));
  }, [brandsLoading, selectedBrand, selectedMaterialId, search]);

  // Build grouped material sections from the flat rolls list. This is purely
  // client-side — no extra API calls. Sections follow the order of the
  // `materials` array returned by the API so ordering is always consistent.
  const materialSections = useMemo<MaterialSection[]>(() => {
    if (selectedMaterialId !== "all" || rolls.length === 0 || materials.length === 0) return [];

    const byMaterialId = new Map<number, RetailFilamentRoll[]>();
    for (const roll of rolls) {
      const mid = roll.material?.id;
      if (mid == null) continue;
      if (!byMaterialId.has(mid)) byMaterialId.set(mid, []);
      byMaterialId.get(mid)!.push(roll);
    }

    return materials
      .filter(m => byMaterialId.has(m.id))
      .map(m => {
        const sectionRolls = byMaterialId.get(m.id)!;
        return { material: m, rolls: sectionRolls, hasMore: sectionRolls.length > SECTION_CAP };
      });
  }, [rolls, materials, selectedMaterialId]);

  const getQty = (id: number) => quantities[id] ?? 1;

  const handleAddToCart = async (roll: RetailFilamentRoll) => {
    const qty = getQty(roll.id);
    try {
      await addFilamentToCart(roll.id, qty);
      increment(qty);
      const label = roll.name ?? `${roll.brand?.name ?? ""} ${roll.color_name}`.trim();
      toast.success(t("filaments.addedToCart", { name: label }));
    } catch {
      toast.error(t("filaments.failedToAdd"));
    }
  };

  const handleQtyChange = (rollId: number, qty: number) => {
    setQuantities(q => ({ ...q, [rollId]: qty }));
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  function renderGrid(gridRolls: RetailFilamentRoll[]) {
    return (
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {gridRolls.map(roll => (
          <FilamentCard
            key={roll.id}
            roll={roll}
            qty={getQty(roll.id)}
            onQtyChange={qty => handleQtyChange(roll.id, qty)}
            onAddToCart={() => handleAddToCart(roll)}
          />
        ))}
      </div>
    );
  }

  function renderContent() {
    if (loading) {
      return (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <FilamentCardSkeleton key={i} />
          ))}
        </div>
      );
    }

    if (rolls.length === 0) {
      return (
        <p className="text-center text-muted-foreground py-12">{t("filaments.noResults")}</p>
      );
    }

    // Specific material selected → flat grid
    if (selectedMaterialId !== "all") {
      return renderGrid(rolls);
    }

    // "All" selected with groupable data → material sections
    if (materialSections.length > 0) {
      return (
        <div className="space-y-10">
          {materialSections.map(section => (
            <section key={section.material.id} aria-label={section.material.name}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold tracking-tight">
                  {section.material.name}
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    ({section.rolls.length})
                  </span>
                </h2>
                {section.hasMore && (
                  <button
                    type="button"
                    onClick={() => handleSelectMaterial(String(section.material.id))}
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline underline-offset-2"
                  >
                    {t("filaments.viewAll", { count: section.rolls.length })}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>
              {renderGrid(section.rolls.slice(0, SECTION_CAP))}
            </section>
          ))}
        </div>
      );
    }

    // Fallback: rolls exist but none have a material assigned → flat grid
    return renderGrid(rolls);
  }

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-[calc(100vh-64px)] bg-muted text-foreground">
      {/* Hero */}
      <section className="px-6 pt-12 pb-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{t("filaments.title")}</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            {t("filaments.description")}
          </p>
        </div>
      </section>

      {/* Brand banner – full-width above the two-column layout */}
      {selectedBrand?.banner_url && (
        <div className="max-w-6xl mx-auto px-6 mb-6">
          <div className="rounded-lg overflow-hidden border border-border">
            <img
              key={selectedBrand.id}
              src={selectedBrand.banner_url}
              alt={`${selectedBrand.name} banner`}
              className="w-full object-cover"
            />
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 pb-16 flex flex-col md:flex-row gap-6">
        {/* Sidebar: brand tabs */}
        <aside className="md:w-48 shrink-0">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{t("filaments.brands")}</div>
          <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0">
            {brandsLoading && brands.length === 0 ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="shrink-0 h-9 w-full rounded-md" />
              ))
            ) : brands.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("filaments.noBrands")}</p>
            ) : (
              brands.map(brand => (
                <button
                  key={brand.id}
                  type="button"
                  onClick={() => handleSelectBrand(brand)}
                  className={clsx(
                    "shrink-0 text-left rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                    selectedBrand?.id === brand.id
                      ? "bg-primary text-primary-foreground"
                      : "bg-card border border-border",
                  )}
                >
                  {brand.name}
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Filters */}
          <div className="space-y-3 mb-6">
            {/* Search */}
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("filaments.searchPlaceholder")}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Material pill tabs — shown once materials are available */}
            {materials.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedMaterialId === "all" ? "default" : "outline"}
                  size="sm"
                  className="rounded-full"
                  onClick={() => handleSelectMaterial("all")}
                >
                  {t("filaments.all")}
                </Button>
                {materials.map(m => (
                  <Button
                    key={m.id}
                    variant={selectedMaterialId === String(m.id) ? "default" : "outline"}
                    size="sm"
                    className="rounded-full"
                    onClick={() => handleSelectMaterial(String(m.id))}
                  >
                    {m.name}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {renderContent()}
        </div>
      </div>
    </div>
  );
}
